import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { StatusBadge, getStatusDisplay, normalizeStatus } from "@/components/ui/status-badge";
import { PolicyLibraryCard } from "@/components/policy/ResponsivePolicyCard";
import { PolicyCardSkeleton } from "@/components/ui/policy-card-skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Search, FileText, Edit, Clock, CheckCircle, AlertCircle } from "lucide-react";
import { format } from "date-fns";
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { fetchDepartments, Department } from "@/lib/department-utils";

interface Policy {
  id: string;
  title: string;
  department: string | null;
  description: string | null;
  version: number;
  status: 'draft' | 'under_review' | 'pending_approval' | 'approved' | 'rejected';
  created_by: string;
  created_at: string;
  updated_at: string;
  effective_date: string | null;
  next_renewal_date: string | null;
  assigned_reviewers: string[] | null;
  assigned_approvers: string[] | null;
  creator: {
    name: string;
  } | null;
}

const PolicyWorkflowStatus = () => {
  const { status } = useParams<{ status: string }>();
  const [searchTerm, setSearchTerm] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null);
  const [comments, setComments] = useState("");
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | 'send_for_approval'>('approve');
  const [departments, setDepartments] = useState<Department[]>([]);
  const { profile, hasRole, hasAnyRole, roles } = useAuth();
  const { toast } = useToast();

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'draft':
        return {
          title: 'Draft Policies',
          description: 'Policies currently being drafted and not yet submitted for review.',
          icon: Edit,
          color: 'text-muted-foreground'
        };
      case 'review':
        return {
          title: 'Under Review',
          description: 'Policies submitted for review and awaiting feedback.',
          icon: Clock,
          color: 'text-warning'
        };
      case 'pending_approval':
        return {
          title: 'Pending Approval',
          description: 'Policies reviewed and awaiting final approval.',
          icon: Clock,
          color: 'text-blue-600'
        };
      case 'approved':
        return {
          title: 'Approved Policies',
          description: 'Policies that have been approved and are active.',
          icon: CheckCircle,
          color: 'text-success'
        };
      case 'rejected':
        return {
          title: 'Rejected Policies',
          description: 'Policies that were rejected and need revision.',
          icon: AlertCircle,
          color: 'text-destructive'
        };
      default:
        return {
          title: 'Policy Workflow',
          description: 'Manage policies by their current status.',
          icon: FileText,
          color: 'text-foreground'
        };
    }
  };

  const statusInfo = getStatusInfo(status || 'draft');
  const StatusIcon = statusInfo.icon;

  const getStatusFilter = (status: string) => {
    switch (status) {
      case 'draft': return 'draft';
      case 'review': return 'under_review';
      case 'pending_approval': return 'pending_approval';
      case 'approved': return 'approved';
      case 'rejected': return 'rejected';
      default: return 'draft';
    }
  };

  useEffect(() => {
    fetchPolicies();
  }, [status, profile?.company_id]);

  // Fetch departments
  useEffect(() => {
    if (profile?.company_id) {
      fetchDepartments(profile.company_id)
        .then(setDepartments)
        .catch(console.error);
    }
  }, [profile?.company_id]);

  const fetchPolicies = async () => {
    if (!profile?.company_id) return;
    
    try {
      const statusFilter = getStatusFilter(status || 'draft');
      
      let query = supabase
        .from('policies')
        .select('*')
        .eq('company_id', profile.company_id)
        .eq('status', statusFilter);

      // Apply role-based filtering for specific statuses
      if (statusFilter === 'draft') {
        // Only admins and creators can see draft policies
        if (!hasAnyRole(['admin', 'creator'])) {
          setPolicies([]);
          setLoading(false);
          return;
        }
      } else if (statusFilter === 'under_review') {
        // Creators, reviewers and admins can see under review policies
        if (!hasAnyRole(['creator', 'reviewer', 'admin'])) {
          setPolicies([]);
          setLoading(false);
          return;
        }
      } else if (statusFilter === 'pending_approval') {
        // Creators, reviewers, approvers and admins can see pending approval policies
        if (!hasAnyRole(['creator', 'reviewer', 'approver', 'admin'])) {
          setPolicies([]);
          setLoading(false);
          return;
        }
      } else if (statusFilter === 'approved') {
        // All roles can see approved policies
        query = query;
      } else if (statusFilter === 'rejected') {
        // Creators and admins can see rejected policies
        if (!hasAnyRole(['creator', 'admin'])) {
          setPolicies([]);
          setLoading(false);
          return;
        }
      }

      const { data: policiesData, error: policiesError } = await query;

      if (policiesError) throw policiesError;

      if (policiesData && policiesData.length > 0) {
        // Get unique creator IDs
        const creatorIds = [...new Set(policiesData.map(policy => policy.created_by))];
        
        // Fetch creator profiles
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, name')
          .in('id', creatorIds);

        if (profilesError) throw profilesError;

        // Combine policies with creator information
        const policiesWithCreators = policiesData.map(policy => ({
          ...policy,
          creator: profilesData?.find(profile => profile.id === policy.created_by) || null
        }));

        setPolicies(policiesWithCreators);
      } else {
        setPolicies([]);
      }
    } catch (error) {
      console.error('Error fetching policies:', error);
      toast({
        title: "Error",
        description: "Failed to fetch policies",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePolicyAction = async () => {
    if (!selectedPolicy) return;
    
    try {
      let newStatus = '';
      let workflowAction = '';
      
      switch (actionType) {
        case 'send_for_approval':
          newStatus = 'pending_approval';
          workflowAction = 'reviewed';
          break;
        case 'approve':
          newStatus = 'approved';
          workflowAction = 'approved';
          break;
        case 'reject':
          newStatus = 'rejected';
          workflowAction = 'rejected';
          break;
      }

      // Update policy status
      const { error: policyError } = await supabase
        .from('policies')
        .update({ status: newStatus as any })
        .eq('id', selectedPolicy.id);

      if (policyError) throw policyError;

      // Create workflow entry
      const { error: workflowError } = await supabase
        .from('policy_workflow')
        .insert({
          policy_id: selectedPolicy.id,
          company_id: profile?.company_id,
          assigned_to: profile?.id,
          created_by: profile?.id,
          status: newStatus,
          action: workflowAction,
          previous_status: selectedPolicy.status,
          new_status: newStatus,
          comments: comments || null
        });

      if (workflowError) throw workflowError;

      toast({
        title: "Success",
        description: `Policy ${workflowAction} successfully`,
      });

      setActionDialogOpen(false);
      setComments("");
      setSelectedPolicy(null);
      fetchPolicies();
    } catch (error) {
      console.error('Error updating policy:', error);
      toast({
        title: "Error",
        description: "Failed to update policy",
        variant: "destructive"
      });
    }
  };

  const filteredPolicies = policies.filter(policy => {
    const matchesSearch = policy.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (policy.description && policy.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesDepartment = departmentFilter === "all" || policy.department === departmentFilter;
    
    return matchesSearch && matchesDepartment;
  });

  const getDepartmentDisplay = (department: string | null) => {
    if (!department) return 'General';
    return department;
  };

  const canReview = hasRole('reviewer') || hasRole('admin');
  const canApprove = hasRole('approver') || hasRole('admin');
  const canSendForApproval = hasRole('creator') || hasRole('admin');

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <StatusIcon className={`w-8 h-8 ${statusInfo.color}`} />
          <div>
            <h1 className="text-3xl font-medium text-foreground">{statusInfo.title}</h1>
            <p className="text-muted-foreground">{statusInfo.description}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="shadow-card">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search policies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map((dept) => (
                  <SelectItem key={dept.id} value={dept.name}>
                    {dept.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Policy Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <PolicyCardSkeleton key={i} />
          ))
        ) : (
          filteredPolicies.map((policy) => (
            <PolicyLibraryCard
              key={policy.id}
              policy={policy}
              userRoles={roles || []}
              onEditDraft={(policyId) => window.location.href = `/policy-creator?edit=${policyId}`}
              onSendForApproval={(policy) => {
                setSelectedPolicy(policy);
                setActionType('send_for_approval');
                setActionDialogOpen(true);
              }}
              onApprove={(policy) => {
                setSelectedPolicy(policy);
                setActionType('approve');
                setActionDialogOpen(true);
              }}
              onReject={(policy) => {
                setSelectedPolicy(policy);
                setActionType('reject');
                setActionDialogOpen(true);
              }}
            />
          ))
        )}
      </div>

      {!loading && filteredPolicies.length === 0 && (
        <div className="text-center py-12">
          <StatusIcon className={`mx-auto h-12 w-12 ${statusInfo.color} mb-4`} />
          <h3 className="text-lg font-semibold text-muted-foreground mb-2">No policies found</h3>
          <p className="text-sm text-muted-foreground">
            {status === 'draft' ? 'Create a new policy to get started.' : 'No policies match your current filters.'}
          </p>
        </div>
      )}

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'send_for_approval' && 'Send for Approval'}
              {actionType === 'approve' && 'Approve Policy'}
              {actionType === 'reject' && 'Reject Policy'}
            </DialogTitle>
            <DialogDescription>
              {actionType === 'send_for_approval' && 'This policy will be sent to approvers for final approval.'}
              {actionType === 'approve' && 'This policy will be marked as approved and become active.'}
              {actionType === 'reject' && 'This policy will be rejected and returned for revision.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Comments (Optional)</label>
              <Textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder={`Add any ${actionType === 'reject' ? 'rejection reasons or ' : ''}comments...`}
                rows={3}
              />
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setActionDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handlePolicyAction}>
                {actionType === 'send_for_approval' && 'Send for Approval'}
                {actionType === 'approve' && 'Approve Policy'}
                {actionType === 'reject' && 'Reject Policy'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PolicyWorkflowStatus;